<template>
    <div :style="{height: height + 'px', margin: '0 -1rem'}" id="chart"></div>
</template>

<script setup>
import { ref, onMounted } from 'vue';

const props = defineProps({
    chartData: {
        type: Object,
        required: true,
    },
    chartHeight: Number,
});

const height = ref(props.chartHeight || 775);

const setFontFamily = () => {
    window.Highcharts.setOptions({
        chart: {
            style: {
                fontFamily: 'Roboto',
            },
        },
    });
};

const loadChart = () => {
    const chartOptions = {
        accessibility: {
            enabled: false
        },
        tooltip: {
            useHTML: true,
        },
        legend: {
            enabled: false,
        },
        xAxis: {
            visible: false,
        },
        yAxis: {
            visible: false,
        },
        series: [{
            name: '',
            type: 'heatmap',
            colorByPoint: true,
            dataLabels: {
                useHTML: true,
                enabled: true,
                className: 'text-fluid-5 text-center',
                style: {
                    fontSize: 'inherit',
                    color: '#fff',
                },
                format: '{point.value}',
            },
            tooltip: {
                pointFormatter() {
                    const currentValue = this.options.pointValues;
                    return `
                        <div>${currentValue.category}</div>
                        <div>${currentValue.value}%</div>
                    `;
                },
                backgroundColor: '#FFF',
            },
            data: props.chartData.heatMap,
        }],
        drilldown: {
            breadcrumbs: {
                buttonTheme: {
                    fill: '#f7f7f7',
                    padding: 8,
                    stroke: '#cccccc',
                    'stroke-width': 1,
                },
                format: '&larr; Go Back',
                floating: true,
                position: {
                    align: 'right'
                },
                showFullPath: false,
            },
            series: props.chartData.drilldown.map((drilldown) => {
                drilldown.name = '';
                drilldown.type = 'heatmap';
                drilldown.className = drilldown.id;
                drilldown.dataLabels = {
                    enabled: true,
                    useHTML: true,
                    className: 'text-fluid-5 text-center drilldown-label',
                    style: {
                        fontSize: 'inherit',
                        color: '#fff',
                    },
                    format: '{point.value}',
                };
                drilldown.point = {
                    events: {
                        click() {
                            window.location = `${window.route('interactive-charts.home', this.options.pointValues.symbol)}`;
                        },
                    },
                };
                drilldown.tooltip = {
                    backgroundColor: '#000',
                    padding: 250,
                    headerFormat: '',
                    pointFormatter() {
                        const currentValue = this.options.pointValues;
                        let changeClass;
                        if (currentValue.raw.priceChange > 0) {
                            changeClass = 'text-up';
                        } else if (currentValue.raw.priceChange < 0) {
                            changeClass = 'text-down';
                        } else {
                            changeClass = 'text-unch';
                        }

                        currentValue.tradeTime = currentValue.tradeTime.replace(/DST|CST|CDT/, 'CT');

                        return `
                            <div class="font-semibold text-sm">
                                ${currentValue.symbolName} ${currentValue.contractMonthYear}
                            </div>
                            <div>
                                <div class="flex">
                                <span class="mr-auto font-semibold">Open:</span>
                                <span>${currentValue.openPrice}</span>
                                </div>
                                <div class="flex">
                                <span class="mr-auto font-semibold">High:</span>
                                <span>${currentValue.highPrice}</span>
                                </div>
                                <div class="flex">
                                <span class="mr-auto font-semibold">Low:</span>
                                <span>${currentValue.lowPrice}</span>
                                </div>
                                <div class="flex">
                                <span class="mr-auto font-semibold">Last:</span>
                                <span>${currentValue.lastPrice}</span>
                                </div>
                                <div class="flex">
                                <span class="mr-auto font-semibold">Change:</span>
                                <span class=${changeClass}>${currentValue.priceChange} (${currentValue.percentChange})</span>
                                </div>
                                <div class="flex">
                                <span class="mr-auto font-semibold">Time:</span>
                                <span>${currentValue.tradeTime}</span>
                                </div>
                            </div>
                        `;
                    },
                };
                return drilldown;
            }),
            activeDataLabelStyle: {
                color: 'white',
                textDecoration: 'none',
            },
        },
        title: {
            text: undefined,
        },
        credits: {
            enabled: false,
        },
    };

    setFontFamily();
    window.Highcharts.chart('chart', chartOptions);
};

onMounted(() => {
    loadChart();
});
</script>

<style lang="scss">
.homepage-heatmap {

    .id_all, .id_Currencies {
        .drilldown-label {
            font-size: 10px;
        }
    }

    .highcharts-tooltip {
        z-index: 25;
    }
}

.id_all {
    .drilldown-label {
        font-size: 12px;
    }
    .highcharts-label span {
        font-family: 'Roboto Condensed', sans-serif !important;
        font-weight: normal !important;
        white-space: break-spaces !important;
    }
}
</style>
